import { ArgentoInsurancePaymentPeriodicities } from '~/config'
import { getMaybeRefValue } from '~/utils/getMaybeRefValue'

import type { PaymentInfo } from '../models'

export const useCoveragePaymentSumary = ({
  amount,
  discount,
  periodicity,
  argentoInsurancePaymentCondition,
  argentoInsuranceCalculatedPaymentConditions,
}: {
  amount: MaybeRef<number | null | undefined>

  discount?: MaybeRef<
    Pick<PaymentInfo, 'discountType' | 'discountAmount' | 'discountTag'> | null | undefined
  >

  periodicity?: MaybeRef<
    PropsTypesOf<typeof ArgentoInsurancePaymentPeriodicities>['value'] | null | undefined
  >

  argentoInsurancePaymentCondition?: MaybeRef<
    SiteApiSchema<'CondicionPagoModel'> | null | undefined
  >

  argentoInsuranceCalculatedPaymentConditions?: MaybeRef<
    SiteApiSchema<'TarificadorCondicionPagoCalculoModel'> | null | undefined
  >
}) => {
  const discountValue = computed(() => getMaybeRefValue(discount))

  function calculateDiscount(amount: number) {
    const discountType = discountValue.value?.discountType
    const discountAmount = discountValue.value?.discountAmount
    if (Boolean(discountType) && Boolean(discountAmount)) {
      switch (discountType) {
        case 'Monto':
          return amount - (discountAmount ?? 0)
        case 'Porcentaje':
          return amount - (amount * (discountAmount ?? 100)) / 100
      }
    }
  }

  const totalPayment = computed(
    () =>
      getMaybeRefValue(argentoInsuranceCalculatedPaymentConditions)?.primaTotal ??
      getMaybeRefValue(amount)
  )

  const calculatedTotalPayment = computed(() => {
    const baseValue = getMaybeRefValue(amount)
    if (!baseValue) return
    const _periodicity = getMaybeRefValue(periodicity)
    switch (_periodicity) {
      case ArgentoInsurancePaymentPeriodicities.monthly.value:
        return baseValue * 12
      case ArgentoInsurancePaymentPeriodicities.fourMonthly.value:
        return baseValue * 3
      case ArgentoInsurancePaymentPeriodicities.quarterly.value:
        return baseValue * 4
      default:
        return baseValue
    }
  })

  function getTotalPayment({ applyDiscount = false }: { applyDiscount?: boolean } = {}) {
    const amount = totalPayment.value ?? calculatedTotalPayment.value
    if (!amount) return
    if (applyDiscount) return calculateDiscount(amount) ?? amount
    return amount
  }

  const paymentInstallments = computed(() => {
    const _argentoInsuranceCalculatedPaymentCondition = getMaybeRefValue(
      argentoInsuranceCalculatedPaymentConditions
    )

    if (
      !(
        _argentoInsuranceCalculatedPaymentCondition &&
        'cuotas' in _argentoInsuranceCalculatedPaymentCondition
      )
    )
      return

    return _argentoInsuranceCalculatedPaymentCondition.cuotas?.map((item) => ({
      order: item.orden ?? 0,
      name: item.nombre,
      amount: item.monto ?? 0,
      discountedAmount: calculateDiscount(item.monto ?? 0),
    }))
  })

  const calculatedPaymentInstallments = computed(() => {
    const _argentoInsurancePaymentCondition = getMaybeRefValue(argentoInsurancePaymentCondition)

    const total = totalPayment.value

    if (!total || !_argentoInsurancePaymentCondition) return

    const installments = _argentoInsurancePaymentCondition?.condicionPagoCuotas
      ?.filter((i) => i.orden != null)
      .map((i) => {
        const amount = i.porcentaje ? (total * i.porcentaje) / 100 : 0
        return {
          order: i.orden ?? 0,
          name: i.nombre,
          amount,
          discountedAmount: calculateDiscount(amount),
        }
      })
      .sort((a, b) => a.order - b.order)

    return installments?.length ? installments : undefined
  })

  function getPaymentInstallments() {
    return paymentInstallments.value ?? calculatedPaymentInstallments.value
  }

  const todayPayment = computed(() => {
    return paymentInstallments.value?.find((pi) => pi.order === 0)?.amount
  })

  const calculateTodayPayment = computed(() => {
    return calculatedPaymentInstallments.value?.find((pi) => pi.order === 0)?.amount
  })

  function getTodayPayment({ applyDiscount = false }: { applyDiscount?: boolean } = {}) {
    const amount = todayPayment.value ?? calculateTodayPayment.value ?? 0
    const discountedAmount = calculateDiscount(amount) ?? 0
    return applyDiscount ? discountedAmount : amount
  }

  const discountIsValid = computed(() => {
    const discount = getMaybeRefValue(discountValue)
    return (
      Boolean(discount?.discountAmount) &&
      Boolean(discount?.discountType) &&
      Boolean(discount?.discountTag)
    )
  })

  return {
    getTotalPayment,
    getPaymentInstallments,
    getTodayPayment,
    discountIsValid,
  }
}
